import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Eye, EyeOff, Mail, Lock, ArrowRight } from "lucide-react";
import { useQuery } from "@apollo/client";
import client from "@/Graphql/apolloClient";
import {
  AUTHENTICATE_MUTATION,
  USER_ACCOUNT_QUERY,
  Get_Country_Code,
  Get_Currency_Code_Details,
  GET_MARKETPLACE_COUNTRIES
} from "@/Graphql/Queries";
import { GET_MY_CART_ITEMS } from "@/Graphql/cartQueries";
import { generateMockData } from "@/Graphql/MockData";
import { useAuth } from "@/utils/authContext";
import { useCart } from "@/utils/cartContext";
import { useToast } from "@/hooks/use-toast";

// Define login schema locally in this file
const loginSchema = z.object({
  email: z.string().min(1, "Username or email is required"), // Allow both username and email
  password: z.string().min(6, "Password must be at least 6 characters"),
});

type LoginFormData = z.infer<typeof loginSchema>;

interface SignInProps {
  setCurrencyCode: React.Dispatch<React.SetStateAction<string>>;
  // setAccouncUserId: React.Dispatch<React.SetStateAction<string>>; // Note: This should be renamed to setAccountUserId in parent components
  isExploreEnabled?: boolean;
  yourLogo?: string; // Optional prop for logo
  base_path?: string; // Optional prop for base path
  isLandingPage?: boolean; // Optional prop for landing page
}

export default function Login({
  isExploreEnabled,
  setCurrencyCode,
  yourLogo,
  base_path,
  isLandingPage
}: SignInProps) {
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { login, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const { cartCount } = useCart();
  const [isLoginPending, setIsLoginPending] = useState(false);
  const [country, setCountry] = useState<string>('');
  const [countryName, setCountryName] = useState<string>('');
  const [config, setConfig] = useState<any[]>([]);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // // Auto-redirect if already logged in on page load
  // useEffect(() => {
  //   if (isLoggedIn) {
  //     navigate("/");
  //   }
  // }, []); // Empty dependency array - only run on mount

  // Load configuration from JSON file
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_APP_FOLDER_PATH || '/public'}/json/configJson.json?t=${new Date().getTime()};`);
        const data = await response.json();
        const loginConfig = data.find((page: any) => page.name === "User Registration")?.configurations.find((c: any) => c.key === "login");

        if (loginConfig) {
          setConfig(loginConfig.fields);
        }
      } catch (error) {
        console.error("Error fetching config:", error);
      }
    };

    fetchConfig();
  }, []);

  // Helper function to check if a field is enabled in config
  const isFieldEnabled = (key: string) => {
    const fieldConfig = config.find((fields: any) => fields.key === key);
    return fieldConfig ? fieldConfig.default : true;
  };

  // Trigger marketplace countries query after login
  useEffect(() => {
    if (isUserLoggedIn) {
      // Manually fetch marketplace countries after user logs in
      client.query({
        query: GET_MARKETPLACE_COUNTRIES,
        variables: { exposure: "MARKETPLACE" },
        fetchPolicy: 'cache-first'
      }).then((response) => {
        console.log('✅ Marketplace Countries Data:', response.data);
        
        // Create a map of country names to IDs for later use
        if (response.data?.getMarketplaceCountries) {
          const countriesMap: { [key: string]: string } = {};
          response.data.getMarketplaceCountries.forEach((country: any) => {
            countriesMap[country.name] = country.id;
          });
          
          // Store the countries map in localStorage for use in cart data
          localStorage.setItem('countriesNameToIdMap', JSON.stringify(countriesMap));
          console.log('✅ Countries map created:', countriesMap);
        }
      }).catch((error) => {
        console.error('❌ Country Filter API Error:', error);
      });
    }
  }, [isUserLoggedIn]);

  // Get country code on initial load
  useEffect(() => {
    const getCountryCode = async () => {
      try {
        if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
          const mockData = await generateMockData(Get_Country_Code);

          localStorage.setItem("countryId", mockData.data.getCountryCodeNoToken?.countryId);
          setCountry(mockData.data?.getCountryCodeNoToken?.countryId);
          setCountryName(mockData.data?.getCountryCodeNoToken?.countryName);
          localStorage.setItem("countryName", mockData.data.getCountryCodeNoToken?.countryName);
        } else {
          const { data } = await client.query({
            query: Get_Country_Code,
            variables: { ownerId: import.meta.env.VITE_APP_COMPANY_ID }
          });
          if (data && data?.getCountryCodeNoToken) {
            localStorage.setItem("countryId", data?.getCountryCodeNoToken?.countryId);
            setCountry(data?.getCountryCodeNoToken?.countryId);
            setCountryName(data?.getCountryCodeNoToken?.countryName);
            localStorage.setItem("countryName", data.getCountryCodeNoToken?.countryName);
          }
        }
      } catch (error) {
        console.error("Error fetching country details:", error);
      }
    };

    getCountryCode();
  }, []);

  // Fetch currency code when country name changes
  useEffect(() => {
    const fetchCurrencyCode = async (countryName: string) => {
      try {
        if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
          const mockResponse = await generateMockData(Get_Currency_Code_Details);
          const { currencyCode } = mockResponse.data.currencyCodeDetails;
          setCurrencyCode(currencyCode);
          localStorage.setItem("currencyCode", currencyCode);
        } else {
          const { data } = await client.query({
            query: Get_Currency_Code_Details,
            variables: {
              countryCurrencyCode: countryName,
            },
          });

          const currencyDetails = data.currencyCodeDetails;

          if (currencyDetails) {
            const { currencyCode } = currencyDetails;
            setCurrencyCode(currencyCode);
            localStorage.setItem("currencyCode", currencyCode);
          } else {
            console.warn("No currency code details returned for country:", countryName);
          }
        }
      } catch (err) {
        console.error("Fetch Currency Code Error:", err);
      }
    };

    if (countryName) {
      fetchCurrencyCode(countryName);
    }
  }, [countryName, setCurrencyCode]);

  // Handle successful login with potential redirect logic
  const handleSuccessfulLogin = async () => {
    // Set user as logged in to trigger country API query
    setIsUserLoggedIn(true);
    
    // Check for pre-login data and explore flow state
    const preLoginCartItems = localStorage.getItem('preLoginCartItems');
    const preLoginCampaignData = localStorage.getItem('preLoginCampaignData');
    const preLoginExploreFlowData = localStorage.getItem('billboardCart');
    console.log("[SYNC] Pre-login cart items:", preLoginExploreFlowData ? JSON.parse(preLoginExploreFlowData) : 'none found');

    // Preserve pre-login cart data before login clears it
    if (preLoginExploreFlowData) {
      localStorage.setItem('preLoginCartData', preLoginExploreFlowData);
      console.log("[SYNC] Preserved pre-login cart data for sync");
    }

    // Fetch cart data after login and store in localStorage
    try {
      const token = localStorage.getItem("authToken");
      const userId = localStorage.getItem("userId");
      if (token && userId) {
        const { data } = await client.query({
          query: GET_MY_CART_ITEMS, // Query for fetching cart items
          fetchPolicy: "network-only",
          variables: {
            accessToken: token,
            userId: userId,
          }
        });
        console.log('Cart data from API bf condition:', data.cartpageItem);
        if (data?.cartpageItem?.length > 0) {
          // Get the countries map from localStorage
          const countriesMapStr = localStorage.getItem('countriesNameToIdMap');
          const countriesMap = countriesMapStr ? JSON.parse(countriesMapStr) : {};
          
          // Map cart items with country IDs
          const cartDataWithCountryIds = data.cartpageItem.map((item: any) => {
            const countryName = item.cartItemCountry || item.country;
            const countryId = countriesMap[countryName];
            
            console.log(`✅ Cart item - Country: ${countryName}, Country ID: ${countryId}`);
            // localStorage.setItem("finalCountryId", countryId || '');
            
            return {
              ...item,
              cartItemCountry: countryName,
              cartItemCountryId: countryId // Add the mapped country ID
            };
          });
          
          localStorage.setItem("userCartData", JSON.stringify(cartDataWithCountryIds));
          console.log("✅ Cart data fetched and stored with country IDs:", cartDataWithCountryIds);
        }
      }
    } catch (error) {
      console.error("Error fetching cart data after login:", error);
    }

    toast({
      title: "Success",
      description: "Login successful!",
      variant: "default",
    });

    setTimeout(() => {
      // Call the login function from auth context BEFORE navigation
      login();
      console.log("islanding page-->", isLandingPage);

        // Regular flow: Redirect based on cartCount
        if (cartCount > 0) {
          // Cart has items - preserve country selection and go to cart
          console.log('🛒 [NAV] Cart has items, redirecting to cart with preserved country');
          navigate(`${base_path}/mycart`, { replace: true });
        } else {
          // ✅ Cart is empty - clear selectedCountryId but keep sessionDefault
          console.log('📭 [NAV] Cart is empty, clearing selectedCountryId');
          
          localStorage.removeItem('selectedCountryId');
          localStorage.removeItem('cartCountry');
          localStorage.removeItem('cartCountryId');
          localStorage.removeItem('navigationCountryBackup');
          localStorage.removeItem('currencyCode');
          
          // Preserve sessionDefault for future use
          const sessionDefaultCountryId = localStorage.getItem('sessionDefaultCountryId');
          const sessionDefaultCountryName = localStorage.getItem('sessionDefaultCountryName');
          
          console.log('✅ [NAV] Preserved sessionDefault:', {
            sessionDefaultCountryId,
            sessionDefaultCountryName
          });
          
          if (!isLandingPage) {
          // If isLandingPage is false, go to my-campaigns
            console.log('🎯 [NAV] Redirecting to my-campaigns');
          navigate(`${base_path}/my-campaigns`, { replace: true });
        } else {
          // Default fallback to landing page
            console.log('🏠 [NAV] Redirecting to landing page');
          navigate(`${base_path}/`, { replace: true });
          }
        }

      // if (preLoginCampaignData && preLoginExploreFlowData) {
      //   // Explore flow: set explore billboard array, fetch cart, compare, remove duplicates, then add to cart
      //   try {
      //     let cartItems = preLoginCartItems ? JSON.parse(preLoginCartItems) : [];
      //     const campaignData = JSON.parse(preLoginCampaignData);
      //     let exploreFlowData = JSON.parse(preLoginExploreFlowData);

      //     // Step 1: Set explore billboard array (already in exploreFlowData)
      //     // Step 2: Fetch cart (already in cartItems)
      //     // Step 3: Compare both billboard arrays and remove duplicates from exploreFlowData
      //     if (cartItems.length > 0 && Array.isArray(exploreFlowData)) {
      //       const getNormalizedId = (item: any) => item.billboardId || item.id || item.cartItemId;
      //       const cartIds = new Set(cartItems.map(getNormalizedId));
      //       exploreFlowData = exploreFlowData.filter((item: any) => !cartIds.has(getNormalizedId(item)));
      //     }

      //     // Step 4: Run add to cart for remaining exploreFlowData
      //     // You may need to call your add to cart API/mutation here
      //     // Example placeholder:
      //     if (exploreFlowData.length > 0) {

      //       navigate("/mycart", {
      //         replace: true
      //       });
      //     }

      //     navigate("/mycart", {
      //       replace: true
      //     });

      //     // Clear pre-login data
      //     localStorage.removeItem('preLoginCampaignData');
      //     localStorage.removeItem('preLoginExploreFlowData');
      //   } catch (error) {
      //     console.error('Error parsing pre-login data:', error);
      //     toast({
      //       title: "Error",
      //       description: "Failed to restore campaign data.",
      //       variant: "destructive",
      //     });
      //     navigate(`${base_path}/`, { replace: true });
      //   }
      // } else {
      //   // Regular flow: Redirect based on cartCount
      //   if (cartCount > 0) {
      //     navigate(`${base_path}/mycart`, { replace: true });
      //   } else if (!isLandingPage) {
      //     // If isLandingPage is false, go to my-campaigns
      //     navigate(`${base_path}/my-campaigns`, { replace: true });
      //   } else {
      //     // Default fallback to landing page
      //     navigate(`${base_path}/`, { replace: true });
      //   }
      // }
    }, 1200);
  };

  // GraphQL login logic from promotional Login.tsx
  const onSubmit = async (data: LoginFormData) => {
    setIsLoginPending(true);
    try {
      const companyId = import.meta.env.VITE_APP_COMPANY_ID;
      const concatenatedUsername = `${data.email}:${companyId}`;
      let responseData;

      if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
        const mockData: any = await generateMockData(AUTHENTICATE_MUTATION);
        responseData = mockData.data;
      } else {
        const response = await client.mutate({
          mutation: AUTHENTICATE_MUTATION,
          variables: {
            username: concatenatedUsername,
            password: data.password,
          },
        });
        responseData = response.data;
      }

      if (!responseData || !responseData.authenticate) {
        throw new Error("Invalid username or password");
      }

      const token = responseData.authenticate.authData.accessToken;
      if (!token) {
        throw new Error("Invalid username or password");
      }

      localStorage.setItem("authToken", token);

      // Fetch user data after successful authentication
      let userData;
      if (import.meta.env.VITE_APP_MOCK_DATA === "true") {
        const mockUserData: any = await generateMockData(USER_ACCOUNT_QUERY);
        userData = mockUserData.data;
        localStorage.setItem("userAccountData", JSON.stringify(userData.userAccount));
      } else {
        const { data: fetchedUserData } = await client.query({
          query: USER_ACCOUNT_QUERY,
          variables: {
            accessToken: token,
          },
        });
        userData = fetchedUserData;
      }

      if (!userData || !userData.userAccount) {
        throw new Error("Failed to retrieve user details");
      }

      const userAccount = userData.userAccount[0];
      const countryId = userAccount.country.id;
      const userId = userAccount.id;
      const userNameLogin = userAccount.login;
      const userEmail = userAccount.email;
      const companyIdRes = userAccount.companyId;
      const { firstName, lastName } = userAccount;

      // Update country and fetch currency if needed
      if (userAccount.country && userAccount.country.name) {
        setCountry(userAccount.country.name);
        localStorage.setItem("countryName", userAccount.country.name);
        // Fetch currency code for the user's country if needed
        if (userAccount.country.name !== countryName) {
          const fetchCurrencyForUserCountry = async () => {
            try {
              const { data } = await client.query({
                query: Get_Currency_Code_Details,
                variables: {
                  countryCurrencyCode: userAccount.country.name,
                },
              });

              if (data && data.currencyCodeDetails) {
                const { currencyCode } = data.currencyCodeDetails;
                setCurrencyCode(currencyCode);
                localStorage.setItem("currencyCode", currencyCode);
              }
            } catch (error) {
              console.error("Error fetching currency for user country:", error);
            }
          };

          fetchCurrencyForUserCountry();
        }
      }

      // Store all user data
      localStorage.setItem("userAccountData", JSON.stringify(userData.userAccount));
      localStorage.setItem("countryId", countryId);
      localStorage.setItem("userId", userId);
      localStorage.setItem("userNameLogin", userNameLogin);
      localStorage.setItem("userEmail", userEmail);
      localStorage.setItem("companyId", companyIdRes);
      localStorage.setItem("userName", `${firstName} ${lastName}`);

      // Handle the successful login (shows toast and redirects)
      handleSuccessfulLogin();
    } catch (error: any) {
      let errorMsg = "Unable to log in. Please check your credentials and try again.";

      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        errorMsg = error.graphQLErrors[0].message;
      } else if (error.networkError) {
        if (error.networkError.result && error.networkError.result.errors) {
          errorMsg = error.networkError.result.errors[0].message;
        } else {
          errorMsg = "Network error. Please check your connection and try again.";
        }
      } else if (error.message) {
        errorMsg = error.message;
      }

      toast({
        title: "Error",
        description: `${errorMsg}`,
        variant: "destructive",
      });
    } finally {
      setIsLoginPending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Left Side - Branding */}
          <div className="md:w-1/2 bg-white p-12 flex flex-col justify-center">
            <div className="mb-8 flex items-center">
              <Link to="/" className="flex items-center">
                {yourLogo ? (
                  <img
                    src={yourLogo}
                    alt="Logo"
                    className="h-10 w-auto"
                    style={{ maxWidth: 160 }}
                  />
                ) : (
                  <>
                    <div className="w-10 h-10 bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2L2 7v10c0 5.55 3.84 10 9 11 5.16-1 9-5.45 9-11V7l-10-5zM12 9c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z" />
                      </svg>
                    </div>
                    <span className="ml-3 text-xl font-semibold text-gray-900">BillboardHub</span>
                  </>
                )}
              </Link>
            </div>

            <div className="space-y-6">
              <h1 className="text-4xl font-bold text-emerald-600 leading-tight">
                Welcome to OOH<br />
                Media Marketplace
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                Run your next outdoor advertising campaign with just a few clicks
              </p>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="md:w-1/2 bg-gray-50 p-12 flex flex-col justify-center">
            <div className="max-w-sm mx-auto w-full">
              {/* Removed Register and Help buttons as requested */}

              <div className="mb-8">
                <h2 className="text-2xl font-bold text-emerald-600 mb-2">
                  Please enter your<br />
                  login and password
                </h2>
              </div>

              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                {/* Email/Username Field - Only shown if enabled in config */}
                {isFieldEnabled('username Login') && (
                  <div className="space-y-2">
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        type="text"
                        placeholder="Username or Email"
                        {...form.register("email")}
                        className="px-5 w-full h-12 pl-20 pr-4 bg-white border-2 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 placeholder:font-normal focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300 shadow-sm text-sm flex items-center"
                      />
                    </div>
                    {form.formState.errors.email && (
                      <p className="text-sm text-red-600">{form.formState.errors.email.message}</p>
                    )}
                  </div>
                )}

                {/* Password Field - Only shown if enabled in config */}
                {isFieldEnabled('password') && (
                  <div className="space-y-2">
                    <div className="relative">
                      <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        {...form.register("password")}
                        className="px-5 w-full h-12 pl-20 pr-12 bg-white border-2 border-gray-200 rounded-lg text-gray-900 placeholder:text-gray-400 placeholder:font-normal focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none transition-all duration-200 hover:border-gray-300 shadow-sm text-sm flex items-center"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {form.formState.errors.password && (
                      <p className="text-sm text-red-600">{form.formState.errors.password.message}</p>
                    )}
                    <div className="flex justify-end">
                      {isFieldEnabled('forgot password') && (
                        <Link to="/forgot-password">
                          <span className="text-emerald-600 hover:underline cursor-pointer text-sm">
                            Forgot Password?
                          </span>
                        </Link>
                      )}
                    </div>
                  </div>
                )}

                {/* Remember Me & Forgot Password - Only shown if enabled in config */}
                {/* <div className="flex items-center justify-between">
                  {isFieldEnabled('remember me') && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="remember-me"
                        checked={rememberMe}
                        onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                      />
                      <Label htmlFor="remember-me" className="text-sm text-gray-600">
                        Remember me
                      </Label>
                    </div>
                  )}
                  
                  {isFieldEnabled('forgot password') && (
                    <Link to="/forgot-password">
                      <span className="text-emerald-600 hover:underline cursor-pointer text-sm">
                        Forgot password?
                      </span>
                    </Link>
                  )}
                </div> */}

                {/* Login Button - Always shown */}
                <Button
                  type="submit"
                  disabled={isLoginPending}
                  className="w-full h-12 btn-emerald-gradient disabled:opacity-50 text-white font-semibold rounded-lg text-lg group"
                >
                  {isLoginPending ? (
                    "Logging in..."
                  ) : (
                    <>
                      Login
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </Button>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-gray-50 text-gray-500">Or continue with</span>
                  </div>
                </div>

                {/* Removed social login buttons as requested */}

                {/* Note about registration and explore */}
                <div className="text-center text-sm text-gray-600 space-y-3">
                  <div>
                    <span>Don't have an account? </span>
                  </div>
                  <div className="flex flex-col md:flex-row items-center justify-center gap-2">
                    {isFieldEnabled('register') && (
                      <Link to="/sign-up">
                        <Button variant="outline" size="sm" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                          Register
                        </Button>
                      </Link>
                    )}
                    {isExploreEnabled && isFieldEnabled('explore') && (
                      <Link to={`${base_path}/explore`}>
                        <Button variant="outline" size="sm" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                          Explore
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}